import {select, fork, take, put, call} from 'redux-saga/effects';
import {matchPath} from '@computerrock/formation-router';
import {persistenceStates, ACEPartner, efServiceTypes, ampVehicleTypes, ampVehicleFuelTypes, apmContractPartnerVehicleCompatibilityTypes, apmContractPartnerContractStatusTypes, apmACEPartnerTypes, TemporaryRestriction, efServiceAssignmentTypes} from '@ace-de/eua-entity-types';
import * as serviceAssignmentActionTypes from '../serviceAssignmentActionTypes';
import fetchRequest from '../../application/sagas/fetchRequest';
import config from '../../config';
import isTemporaryRestrictionActive from '../../contract-partners/isTemporaryRestrictionActive';
import updateServiceAssignment from './updateServiceAssignment';
import * as serviceCaseHelpers from '../../service-cases/sagas/serviceCaseHelpers';
import routePaths from '../../routePaths';

const filterContractPartnersBySearchQuery = function* filterContractPartnersBySearchQuery(
    serviceCaseId,
    serviceAssignmentLineNo,
    searchQuery,
) {
    const {serviceManager} = yield select(state => state.application);
    const partnerManagementService = serviceManager.loadService('partnerManagementService');
    const googleMapsService = serviceManager.loadService('googleMapsService');
    const {serviceCases} = yield select(state => state.serviceCases);
    const {serviceAssignments} = yield select(state => state.serviceAssignments);
    const {contractPartners} = yield select(state => state.contractPartners);
    const {location} = yield select(state => state.router);
    const serviceCase = serviceCases[serviceCaseId];
    const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
    const serviceAssignment = serviceAssignments[serviceAssignmentId];
    const sapRouteMatch = matchPath(location?.pathname, {
        path: routePaths.SERVICE_ASSIGNMENT_PICKUP,
        exact: true,
    });

    if (!serviceAssignment || !serviceCase.damage || !serviceCase.damage.location) return;

    // get current CP route related data from previous recommendations if possible
    // in order to avoid additional calls (distances and route)
    const currentContractPartner = serviceAssignment?.acePartner?.id
        ? serviceAssignment.contractPartnerRecommendations.length
            ? serviceAssignment.contractPartnerRecommendations.find(contractPartner => (
                contractPartner.id === serviceAssignment.acePartner.id
            ))
            : serviceAssignment.acePartner
        : null;

    yield put({
        type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
        payload: {persistenceState: persistenceStates.PENDING},
    });

    yield put({
        type: serviceAssignmentActionTypes.SET_CONTRACT_PARTNER_RECOMMENDATIONS,
        payload: {serviceAssignmentId, contractPartnerRecommendationDTOs: []},
    });

    yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);

    const isInitialAssignment = serviceAssignment.lineNo === 'FBS1'
        || parseInt(serviceAssignment.lineNo, 10) === 1
        || serviceAssignment.isCloneFromInitial;
    const damageLocation = isInitialAssignment
        ? serviceCase.damage.location
        : serviceAssignment.serviceLocation;
    const {pickupLocation, destination} = serviceAssignment;

    if ((!sapRouteMatch && !damageLocation) || (sapRouteMatch && (!pickupLocation || !destination))) return;

    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('searchTerm', searchQuery);
    searchQueryParams.append('size', `${config.CONTRACT_PARTNER_RECOMMENDATION_COUNT}`);
    searchQueryParams.append('contractStatus', apmContractPartnerContractStatusTypes.ACTIVE);
    searchQueryParams.append('partnerType', apmACEPartnerTypes.CONTRACT_PARTNER);

    yield fork(
        fetchRequest,
        serviceAssignmentActionTypes.SEARCH_CONTRACT_PARTNERS_BY_SEARCH_QUERY_REQUEST,
        partnerManagementService.getContractPartners, {
            searchQueryParams,
        },
    );

    const apmResponseAction = yield take([
        serviceAssignmentActionTypes.SEARCH_CONTRACT_PARTNERS_BY_SEARCH_QUERY_REQUEST_SUCCEEDED,
        serviceAssignmentActionTypes.SEARCH_CONTRACT_PARTNERS_BY_SEARCH_QUERY_REQUEST_FAILED,
    ]);

    if (apmResponseAction.error) {
        yield put({
            type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
            payload: {persistenceState: persistenceStates.FAILED},
        });
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: apmResponse} = apmResponseAction.payload;
    const {contractPartnerDTOs} = apmResponse;

    if (!contractPartnerDTOs.length) {
        yield put({
            type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
            payload: {persistenceState: persistenceStates.READY},
        });
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {acePartnerDTOs} = yield call(partnerManagementService.getACEPartnersById, {
        acePartnerIds: contractPartnerDTOs
            .map(acePartnerDTO => acePartnerDTO['id'])
            .filter(Boolean),
    });

    yield fork(
        fetchRequest,
        serviceAssignmentActionTypes.SORT_CONTRACT_PARTNERS_BY_DISTANCE_REQUEST,
        googleMapsService.sortDistances,
        {
            origin: {
                location:
                    {
                        latLng: {
                            latitude: sapRouteMatch ? pickupLocation.latitude : damageLocation.latitude,
                            longitude: sapRouteMatch ? pickupLocation.longitude : damageLocation.longitude,
                        },
                    },
            },
            destinations: acePartnerDTOs.map(acePartnerDTO => ({
                apiUnrelatedFields: acePartnerDTO,
                location: {
                    latLng: {
                        latitude: acePartnerDTO.location.latitude,
                        longitude: acePartnerDTO.location.longitude,
                    },
                },
            })),
        },
    );

    const sortDistancesResponseAction = yield take([
        serviceAssignmentActionTypes.SORT_CONTRACT_PARTNERS_BY_DISTANCE_REQUEST_SUCCEEDED,
        serviceAssignmentActionTypes.SORT_CONTRACT_PARTNERS_BY_DISTANCE_REQUEST_FAILED,
    ]);

    if (sortDistancesResponseAction.error) {
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: sortedCpResponse} = sortDistancesResponseAction.payload;
    const {distances} = sortedCpResponse;

    const contractPartnersWithPickupRouteData = {};

    if (sapRouteMatch) {
        yield fork(
            fetchRequest,
            serviceAssignmentActionTypes.GET_CONTRACT_PARTNERS_DISTANCE_TO_DESTINATION_LOCATION_REQUEST,
            googleMapsService.sortDistances,
            {
                origin: {
                    location: {
                        latLng: {
                            latitude: destination.latitude,
                            longitude: destination.longitude,
                        },
                    },
                },
                destinations: acePartnerDTOs.map(acePartnerDTO => ({
                    apiUnrelatedFields: acePartnerDTO,
                    location: {
                        latLng: {
                            latitude: acePartnerDTO.location.latitude,
                            longitude: acePartnerDTO.location.longitude,
                        },
                    },
                })),
            },
        );

        const distanceToDestinationLocationResponseAction = yield take([
            serviceAssignmentActionTypes.GET_CONTRACT_PARTNERS_DISTANCE_TO_DESTINATION_LOCATION_REQUEST_SUCCEEDED,
            serviceAssignmentActionTypes.GET_CONTRACT_PARTNERS_DISTANCE_TO_DESTINATION_LOCATION_REQUEST_FAILED,
        ]);

        if (distanceToDestinationLocationResponseAction.error) return;

        const {response: distanceToDestinationLocationResponse} = distanceToDestinationLocationResponseAction.payload;
        const {distances: distancesToDestinationLocation} = distanceToDestinationLocationResponse;

        distancesToDestinationLocation.forEach(distanceToDestinationLocation => {
            const {id} = distanceToDestinationLocation.destination.apiUnrelatedFields;
            contractPartnersWithPickupRouteData[id] = distanceToDestinationLocation;
        });
    }

    const currentContractPartnerStatus = serviceAssignment?.acePartner?.id
    && contractPartners
    && contractPartners[serviceAssignment.acePartner.id]
        ? contractPartners[serviceAssignment.acePartner.id].contractStatus || null
        : null;
    const shouldDisplayRecommendedCP = currentContractPartnerStatus === apmContractPartnerContractStatusTypes.ACTIVE;
    const shouldAddExistingACEPartnerAsRecommended = serviceAssignment.acePartner
        && !acePartnerDTOs.find(contractPartnerDTO => (contractPartnerDTO.id === serviceAssignment.acePartner.id))
        && shouldDisplayRecommendedCP;
    let containingContractPartners = null;

    if (!shouldAddExistingACEPartnerAsRecommended) {
        const recommendedContractPartnerPolygons = googleMapsService.checkIsLocationInsidePolygons({
            longitude: sapRouteMatch ? pickupLocation.longitude : damageLocation.longitude,
            latitude: sapRouteMatch ? pickupLocation.latitude : damageLocation.latitude,
            polygons: acePartnerDTOs.map(acePartnerDTO => acePartnerDTO.fixPricePolygon).filter(Boolean),
        });
        containingContractPartners = acePartnerDTOs.filter(acePartnerDTO => (
            recommendedContractPartnerPolygons.includes(acePartnerDTO.fixPricePolygon)
        ));
    }

    const contractPartnerRecommendationDTOs = distances.map(distance => {
        const contractPartner = distance.destination.apiUnrelatedFields;
        const isServiceTypeBasicOne = [
            efServiceTypes.RECOVERY,
            efServiceTypes.ROADSIDE_ASSISTANCE,
            efServiceTypes.TOWING,
        ].includes(serviceCase.serviceType);
        const [service] = contractPartner?.services
            ? contractPartner.services.filter(service => service.serviceType === serviceCase.serviceType) : [];

        const additionalFields = {
            ...(service && {
                ...(isServiceTypeBasicOne && {
                    isVehicleOverweight: service.tonnageThreshold === config.MIN_ULTRA_HEAVY_WEIGHT_VEHICLE_TONNAGE
                        ? false
                        : (serviceCase?.vehicle?.weight
                            ? !((service.tonnageThreshold || 0) >= serviceCase.vehicle.weight)
                            : false),
                    isCamperIncompatible: !((service.camperDimensions
                            && serviceCase?.vehicle?.type === ampVehicleTypes.CAMPER
                            && Object.keys(service.camperDimensions).every(key => (
                                typeof service.camperDimensions[key] !== 'number'
                                || !service.camperDimensions[key]
                                || typeof serviceCase.vehicle[key] !== 'number'
                                || service.camperDimensions[key] >= serviceCase.vehicle[key])))
                        || !(!service.camperDimensions && serviceCase.vehicle?.type === ampVehicleTypes.CAMPER
                            && (serviceCase.vehicle?.weight || serviceCase.vehicle?.length
                                || serviceCase.vehicle?.width || serviceCase.vehicle?.height))),
                }),
                ...(serviceCase?.vehicle?.type === ampVehicleTypes.MOTORBIKE && {
                    isVehicleTypeIncompatible: !(contractPartner?.vehicleCompatibility?.length
                        ? contractPartner.vehicleCompatibility
                            .includes(apmContractPartnerVehicleCompatibilityTypes.MOTORBIKE)
                        : false),
                }),
                ...([ampVehicleFuelTypes.GAS, ampVehicleFuelTypes.HYBRID].includes(serviceCase?.vehicle?.fuelType)
                    && {
                        isVehicleFuelTypeIncompatible: !(serviceCase.vehicle.fuelType === ampVehicleFuelTypes.GAS
                            ? !!contractPartner?.vehicleCompatibility
                                ?.includes(apmContractPartnerVehicleCompatibilityTypes.CNG_LNG)
                            : !!contractPartner?.vehicleCompatibility
                                ?.includes(apmContractPartnerVehicleCompatibilityTypes.HYBRID)),
                    }),
            }),
        };

        return {
            ...contractPartner,
            routeToDamageLocation: {
                totalKilometers: distance.distanceMeters / 1000,
                totalTravelTime: Number(distance.duration.replace('s', '')) / 60,
            },
            ...(contractPartnersWithPickupRouteData[contractPartner.id] && {
                routeToPickupLocation: {
                    totalKilometers: contractPartnersWithPickupRouteData[contractPartner.id].distanceMeters / 1000,
                    totalTravelTime: Number(
                        contractPartnersWithPickupRouteData[contractPartner.id].duration.replace('s', ''),
                    ) / 60,
                },
            }),
            location: {
                latitude: contractPartner.location?.latitude,
                longitude: contractPartner.location?.longitude,
            },
            isServiceUnsupported: !service,
            ...(additionalFields),
        };
    });

    if (contractPartnerRecommendationDTOs.length > 0) {
        const restrictionsMap = yield call(partnerManagementService.getACEPartnersRestrictions, {
            acePartnerIds: contractPartnerRecommendationDTOs
                .map(contractPartnerRecommendationDTO => contractPartnerRecommendationDTO.id)
                .filter(Boolean),
        });
        contractPartnerRecommendationDTOs.forEach(contractPartnerRecommendationDTO => {
            if (restrictionsMap && restrictionsMap[contractPartnerRecommendationDTO.id]) {
                const temporaryRestrictions = new Map();
                restrictionsMap[contractPartnerRecommendationDTO.id]
                    .forEach(restriction => {
                        const temporaryRestriction = new TemporaryRestriction().fromDTO(restriction);
                        if (isTemporaryRestrictionActive(temporaryRestriction)) {
                            temporaryRestrictions.set(temporaryRestriction.id, temporaryRestriction);
                        }
                    });
                contractPartnerRecommendationDTO.temporaryRestrictions = temporaryRestrictions;
            }
        });
    }

    const recommendedContractPartner = shouldAddExistingACEPartnerAsRecommended
        ? currentContractPartner
        : containingContractPartners?.[0] || contractPartnerRecommendationDTOs[0];

    // save recommended contract partner as selected if no contract partner was previously set
    if (!serviceAssignment.acePartner && recommendedContractPartner) {
        const recommendedACEPartner = new ACEPartner()
            .fromDTO(contractPartners[recommendedContractPartner.id] || null);
        yield* updateServiceAssignment({
            caller: 'LOAD_SAV_CONTRACT_PARTNER_RECOMMENDATIONS',
            assignmentType: efServiceAssignmentTypes.VEHICLE,
            serviceAssignmentLineNo,
            serviceCaseId,
            serviceAssignmentData: {
                acePartner: {
                    ...recommendedACEPartner,
                    ...(recommendedACEPartner.businessContactDetails
                        ? {
                            businessContactDetails: {
                                ...recommendedACEPartner.businessContactDetails,
                                phoneNo: recommendedACEPartner?.emergencyContacts?.find(contact => contact.is24h7Emergency)?.phoneNo || '',
                            },
                            contactDetails: null,
                        } : {}
                    ),
                },
            },
        });

        // set persistence state back to PENDING
        yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);
    }

    yield put({
        type: serviceAssignmentActionTypes.SET_CONTRACT_PARTNER_RECOMMENDATIONS,
        payload: {
            serviceAssignmentId,
            contractPartnerRecommendationDTOs: [
                ...(shouldAddExistingACEPartnerAsRecommended ? [currentContractPartner] : []),
                ...contractPartnerRecommendationDTOs,
            ],
            recommendedContractPartnerId: recommendedContractPartner?.id || null,
            // if several CPs have damage location inside their polygons (overlapping polygons)
            // we need them all as recommended CPs
            ...(containingContractPartners?.length > 1 && {
                recommendedContractPartnerIds: containingContractPartners.map(containingContractPartner => (
                    containingContractPartner.id
                )),
            }),
        },
    });

    yield put({
        type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
        payload: {persistenceState: persistenceStates.READY},
    });

    yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
};

export default filterContractPartnersBySearchQuery;
